import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, RefreshCw, Tag, ToggleLeft, ToggleRight,
  BookMarked, CheckCircle2, ChevronDown, ChevronUp, Zap,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';

const inputCls = 'border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';
const MATCH_TYPES = ['contains', 'starts_with', 'exact'];

// ── Smart Rules (auto-built from Xero history) ──────────────────────────────

function SmartRulesSection() {
  const [rules, setRules]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [building, setBuilding]         = useState(false);
  const [applying, setApplying]         = useState(false);
  const [collapsed, setCollapsed]       = useState(false);
  const [search, setSearch]             = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/accounts/categorization-rules');
      setRules(res.data || []);
    } catch {
      toast.error('Failed to load smart rules');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // The old builder behind this button is disarmed and answers 410. It made
  // 628 rules from a single observation each, every one at confidence 1.00.
  // The replacement needs at least two observations before it will write a
  // rule, weights evidence by age, refuses bank and card accounts and account
  // codes the chart does not contain, and records how much evidence each rule
  // actually has -- so a rule can be checked instead of trusted.
  const rebuild = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post('/api/categorization/rules/rebuild');
      toast.success(
        `${data.total_rules} rule(s) from ${data.observations} observation(s). ` +
        `Skipped ${data.skipped_single_observation} with only one.`
      );
      load();
    } catch { toast.error('Rule rebuild failed'); }
    setBuilding(false);
  };

  // Likewise disarmed: applying the old set booked all 362 transfers and
  // card payments as meal expenses through a three-character substring rule.
  const applyRules = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/api/categorization/run',
                                      { only_undecided: true });
      toast.success(
        `${data.decided} decided — ${data.auto} automatic, ` +
        `${data.suggested} suggested, ${data.review} for review.`
      );
    } catch { toast.error('Categorization run failed'); }
    setApplying(false);
  };

  const toggleRule = async (ruleId, current) => {
    await api.patch(`/api/accounts/categorization-rules/${ruleId}`, { is_active: !current });
    setRules(r => r.map(x => x.rule_id === ruleId ? { ...x, is_active: !current } : x));
  };

  const deleteRule = async (ruleId) => {
    await api.delete(`/api/accounts/categorization-rules/${ruleId}`);
    setRules(r => r.filter(x => x.rule_id !== ruleId));
    toast.success('Rule deleted');
  };

  const filtered = search
    ? rules.filter(r =>
        r.match_value.toLowerCase().includes(search.toLowerCase()) ||
        r.account_code.toLowerCase().includes(search.toLowerCase()) ||
        (r.account_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : rules;

  const contactCount = rules.filter(r => r.match_type === 'contact_name').length;
  const keywordCount = rules.filter(r => r.match_type === 'keyword').length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6" data-testid="smart-rules-section">
      {/* Header */}
      <div className="px-5 py-4 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-900 rounded-lg flex items-center justify-center">
            <BookMarked size={14} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-sm text-indigo-900">Historical rules — built from evidence</p>
            <p className="text-xs text-indigo-600 mt-0.5">
              {rules.length > 0
                ? `${rules.length.toLocaleString()} rules derived from your transaction history (${contactCount} contacts + ${keywordCount} keywords)`
                : 'Derived from repeated history. A merchant seen once does not make a rule.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={applyRules}
            disabled={applying || rules.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
            data-testid="apply-smart-rules-btn"
          >
            <CheckCircle2 size={12} /> {applying ? 'Categorizing…' : 'Categorize Uncategorized'}
          </button>
          <button
            onClick={rebuild}
            disabled={building}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-900 text-white text-xs font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50"
            data-testid="rebuild-smart-rules-btn"
          >
            <RefreshCw size={12} className={building ? 'animate-spin' : ''} />
            {building ? 'Building…' : rules.length === 0 ? 'Build from History' : 'Re-build'}
          </button>
          <button
            onClick={() => setCollapsed(p => !p)}
            className="p-1.5 text-indigo-500 hover:text-indigo-900"
            data-testid="collapse-smart-rules-btn"
          >
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Search */}
          {rules.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search rules by vendor, keyword, or account…"
                className="w-full text-xs border border-slate-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                data-testid="smart-rules-search"
              />
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="p-5 space-y-2">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" />)}
            </div>
          ) : rules.length === 0 ? (
            <div className="py-14 text-center">
              <BookMarked size={32} className="text-slate-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600 mb-1">No smart rules yet</p>
              <p className="text-xs text-slate-400 mb-4">Click "Build from History" to generate rules from your imported transaction data.</p>
              <button
                onClick={rebuild}
                disabled={building}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-lg hover:bg-indigo-800 disabled:opacity-50"
              >
                <Zap size={14} /> {building ? 'Building…' : 'Build Rules from History'}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-xs" data-testid="smart-rules-table">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Type</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Match Value</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Account</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Confidence</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Matches</th>
                    <th className="text-center px-4 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Active</th>
                    <th className="px-4 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(rule => (
                    <tr
                      key={rule.rule_id}
                      className={`hover:bg-slate-50 ${!rule.is_active ? 'opacity-40' : ''}`}
                      data-testid={`smart-rule-row-${rule.rule_id}`}
                    >
                      <td className="px-4 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${rule.match_type === 'contact_name' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {rule.match_type === 'contact_name' ? 'Contact' : 'Keyword'}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-medium text-slate-800 max-w-xs">
                        <span className="truncate block max-w-[220px]" title={rule.match_value}>{rule.match_value}</span>
                      </td>
                      <td className="px-4 py-2 font-mono">
                        <span className="text-slate-700 font-semibold">{rule.account_code}</span>
                        {rule.account_name && (
                          <span className="text-slate-500 ml-1">— {rule.account_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-mono font-bold ${rule.confidence >= 0.9 ? 'text-emerald-600' : rule.confidence >= 0.7 ? 'text-amber-600' : 'text-red-500'}`}>
                          {(rule.confidence * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-slate-500">{rule.match_count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => toggleRule(rule.rule_id, rule.is_active)}
                          className="text-slate-400 hover:text-indigo-600"
                          data-testid={`toggle-smart-rule-${rule.rule_id}`}
                        >
                          {rule.is_active
                            ? <ToggleRight size={16} className="text-emerald-500" />
                            : <ToggleLeft size={16} />}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => deleteRule(rule.rule_id)}
                          className="text-slate-300 hover:text-red-500"
                          data-testid={`delete-smart-rule-${rule.rule_id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {search && filtered.length === 0 && (
                <div className="py-6 text-center text-slate-400 text-xs">No rules match "{search}"</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Manual Keyword Rules ─────────────────────────────────────────────────────

function ManualRulesSection() {
  const [rules, setRules]       = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ keyword: '', account_id: '', match_type: 'contains', priority: 10, entity: '' });

  const expenseAccounts = accounts.filter(a => ['expense', 'revenue', 'cogs'].includes(a.account_type));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, aRes] = await Promise.all([
        api.get('/api/accounts/merchant-rules'),
        api.get('/api/accounts/chart'),
      ]);
      setRules(rRes.data || []);
      setAccounts(aRes.data || []);
    } catch { console.error('Failed to load manual rules'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.keyword || !form.account_id) { toast.error('Keyword and account are required'); return; }
    setSaving(true);
    try {
      await api.post('/api/accounts/merchant-rules', form);
      setShowAdd(false);
      setForm({ keyword: '', account_id: '', match_type: 'contains', priority: 10, entity: '' });
      load();
      toast.success('Rule created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create rule'); }
    setSaving(false);
  };

  const toggle = async (ruleId, isActive) => {
    await api.put(`/api/accounts/merchant-rules/${ruleId}`, { is_active: !isActive });
    load();
  };

  const remove = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    await api.delete(`/api/accounts/merchant-rules/${ruleId}`);
    setRules(r => r.filter(x => x.rule_id !== ruleId));
    toast.success('Rule deleted');
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="manual-rules-section">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
            <Tag size={14} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-sm text-slate-800">Manual Keyword Rules</p>
            <p className="text-xs text-slate-500 mt-0.5">Manually-defined keyword rules for Plaid bank import categorization</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-100"
          >
            <RefreshCw size={13} className="text-slate-500" />
          </button>
          <button
            onClick={() => setShowAdd(p => !p)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-md hover:bg-slate-700"
            data-testid="add-manual-rule-btn"
          >
            <Plus size={13} /> Add Rule
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="border-b border-slate-100 px-5 py-4 bg-slate-50" data-testid="add-manual-rule-form">
          <p className="text-xs font-semibold text-slate-700 mb-3">New Keyword Rule</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Keyword *</label>
              <input
                value={form.keyword}
                onChange={e => setForm(f => ({ ...f, keyword: e.target.value }))}
                placeholder="e.g. AWS, Stripe"
                className={inputCls + ' w-full'}
                data-testid="manual-rule-keyword-input"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500 mb-1 block">Map to Account *</label>
              <select
                value={form.account_id}
                onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
                className={inputCls + ' w-full'}
                data-testid="manual-rule-account-select"
              >
                <option value="">— Select account —</option>
                {expenseAccounts.map(a => (
                  <option key={a.account_id} value={a.account_id}>{a.account_code} {a.account_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Match Type</label>
              <select
                value={form.match_type}
                onChange={e => setForm(f => ({ ...f, match_type: e.target.value }))}
                className={inputCls + ' w-full'}
              >
                {MATCH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Priority</label>
              <input
                type="number"
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 10 }))}
                className={inputCls + ' w-full'}
                min={1}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Entity (optional)</label>
              <select
                value={form.entity}
                onChange={e => setForm(f => ({ ...f, entity: e.target.value }))}
                className={inputCls + ' w-full'}
              >
                <option value="">All Entities</option>
                <option value="Eyvo USA">Eyvo USA</option>
                <option value="Eyvo India">Eyvo India</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-slate-800 text-white text-xs rounded-md hover:bg-slate-700 disabled:opacity-50"
              data-testid="save-manual-rule-btn"
            >
              {saving ? 'Saving…' : 'Save Rule'}
            </button>
            <button onClick={() => setShowAdd(false)} className="text-xs text-slate-500 px-2">Cancel</button>
          </div>
        </div>
      )}

      {/* Rules list */}
      {loading ? (
        <div className="p-5 text-center text-slate-400 text-sm">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="py-10 text-center">
          <Tag size={28} className="text-slate-200 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600 mb-0.5">No manual rules yet</p>
          <p className="text-xs text-slate-400">Add rules to auto-categorize new incoming Plaid bank transactions.</p>
        </div>
      ) : (
        <table className="w-full text-xs" data-testid="manual-rules-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Priority</th>
              <th className="text-left px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Keyword</th>
              <th className="text-left px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Match Type</th>
              <th className="text-left px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Maps To Account</th>
              <th className="text-left px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Entity</th>
              <th className="text-right px-5 py-2.5 font-semibold text-slate-500 uppercase text-[10px] tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rules.map(rule => (
              <tr key={rule.rule_id} className={`hover:bg-slate-50 ${!rule.is_active ? 'opacity-50' : ''}`} data-testid={`manual-rule-row-${rule.rule_id}`}>
                <td className="px-5 py-2.5 font-mono text-slate-500 w-16">{rule.priority}</td>
                <td className="px-5 py-2.5">
                  <code className="bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded font-mono">{rule.keyword}</code>
                </td>
                <td className="px-5 py-2.5 text-slate-500">{rule.match_type}</td>
                <td className="px-5 py-2.5">
                  <p className="font-medium text-slate-800">{rule.account_name}</p>
                  <p className="font-mono text-slate-400">{rule.account_code}</p>
                </td>
                <td className="px-5 py-2.5 text-slate-500">{rule.entity || 'All'}</td>
                <td className="px-5 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => toggle(rule.rule_id, rule.is_active)}
                      className="text-slate-400 hover:text-indigo-700"
                      data-testid={`toggle-manual-rule-${rule.rule_id}`}>
                      {rule.is_active ? <ToggleRight size={16} className="text-emerald-500" /> : <ToggleLeft size={16} />}
                    </button>
                    <button onClick={() => remove(rule.rule_id)}
                      className="text-slate-300 hover:text-red-500"
                      data-testid={`delete-manual-rule-${rule.rule_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Help text */}
      <div className="px-5 py-3 bg-blue-50 border-t border-blue-100">
        <p className="text-[10px] text-blue-600 font-semibold mb-1">How Manual Rules Work</p>
        <div className="flex flex-wrap gap-x-6 gap-y-0.5 text-[10px] text-blue-500">
          <span><strong>contains</strong> — keyword appears anywhere in the description</span>
          <span><strong>starts_with</strong> — description starts with keyword</span>
          <span><strong>exact</strong> — exact full description match</span>
          <span>Evaluated in priority order (lowest number first)</span>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function MerchantRules() {
  return (
    <div className="p-6 fade-in" data-testid="merchant-rules-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Auto-Categorize Rules</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Smart rules are auto-derived from your historical data. Manual rules apply to new incoming Plaid transactions.
        </p>
      </div>
      <SmartRulesSection />
      <ManualRulesSection />
    </div>
  );
}
